/**
 * The terms a person accepts, and where the legal pages are (F-0554,
 * `docs/concept-outbound-email-and-account-verification.md` §12).
 *
 * The pages themselves live on the website (`code/website/src/pages/legal/`), whose origin a
 * deployment names in `/.well-known/featit` (`websiteOrigin`). No app hardcodes a host.
 */

/**
 * The terms in force. An account whose recorded acceptance names another version is asked again,
 * on one blocking screen, before it next works (AC-TA4, AC-TA5).
 *
 * ⚠️ Changing this value is the act that asks every account again. Change it when the terms
 * change, never for an edit that changes nothing a person agreed to.
 */
export const TERMS_VERSION = '2026-10-10';

/**
 * PX-14 (D-0430): the version of the sentence a customer's first tap accepts the terms beside — the
 * sign-up's own line (`auth.legal.acceptanceLine`), shown while their invitation opens. Recorded with
 * the acceptance (`users.terms_acceptance_wording`), so what was on the screen is known. Change it
 * whenever that sentence changes.
 */
export const FIRST_TAP_TERMS_WORDING = 'first-tap/2026-10-28';

/** The website's legal pages, as paths on its origin. */
export const LEGAL_PAGE_PATHS = {
  imprint: '/legal/imprint',
  privacy: '/legal/privacy',
  terms: '/legal/terms',
} as const;

export interface ILegalLinks {
  readonly imprint: string;
  readonly privacy: string;
  readonly terms: string;
}

/**
 * The three addresses on a website origin, or `null` without a usable one: a link to a guessed
 * host is worse than a sentence without links.
 */
export function legalLinks(websiteOrigin: string | null | undefined): ILegalLinks | null {
  const raw = websiteOrigin?.trim();
  if (!raw) return null;
  let origin: string;
  try {
    const url = new URL(raw);
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
    origin = url.origin;
  } catch {
    return null;
  }
  return {
    imprint: `${origin}${LEGAL_PAGE_PATHS.imprint}`,
    privacy: `${origin}${LEGAL_PAGE_PATHS.privacy}`,
    terms: `${origin}${LEGAL_PAGE_PATHS.terms}`,
  };
}

/** Whether an account must accept the terms before it next works (AC-TA3). */
export function termsAcceptanceRequired(acceptedVersion: string | null | undefined): boolean {
  return acceptedVersion !== TERMS_VERSION;
}

/**
 * I-2273 (AC-TA15): the code every door's refusal for terms not yet accepted carries: the API's 403,
 * sign-up's 400 and the one-tap entry's 400. A client that reads it shows its terms screen.
 */
export const TERMS_ACCEPTANCE_REQUIRED_CODE = 'terms_acceptance_required';

/**
 * I-2273 (AC-TA15, AC-TA18): the credentials a person signs in with, held to the terms until their
 * account accepts the version in force. An agent's, a bridge's and an integration's are not: a
 * running agent is not stranded the moment the version changes.
 */
export const TERMS_HELD_CREDENTIAL_KINDS: readonly string[] = ['session', 'as-if', 'verification'];

/** Whether a credential of `kind`, whose person accepted `acceptedVersion`, is held at the API. */
export function termsHoldCredential(
  kind: string | null | undefined,
  acceptedVersion: string | null | undefined,
): boolean {
  return (
    !!kind && TERMS_HELD_CREDENTIAL_KINDS.includes(kind) && termsAcceptanceRequired(acceptedVersion)
  );
}
